import type { PostingEvent } from "@/lib/api/catalogue";
import type { ApplicationEvent } from "@/lib/api/envoy";
import { lookup } from "@/lib/lookup";
import { text } from "./payload";
import {
    ENVOY_KIND,
    CATALOGUE_KIND,
    catalogueDetail,
    envoyDetail,
    kindWords,
} from "./recordDetail";

/** The record: every event from the catalogue and from envoy, as rows. */

export type RecordTone = "board" | "match" | "queue" | "tailor" | "sent" | "fault" | "quiet";

export interface RecordEntry {
    id: string;
    service: "catalogue" | "envoy";
    seq: number;
    /** "Discovered", "Tailored". */
    kind: string;
    at: string;
    tone: RecordTone;
    detail: string;
    /** A resume the entry names, to open. */
    resumeId: string | null;
}

/**
 * Every event from the catalogue and from envoy, oldest first. Two at
 * the same instant keep the catalogue's first, then each record's order.
 */
export function postingRecord(
    postingEvents: readonly PostingEvent[],
    applicationEvents: readonly ApplicationEvent[],
    modelName: (model: string) => string = (model) => model,
    timeZone?: string,
): RecordEntry[] {
    const catalogue: RecordEntry[] = [...postingEvents]
        .sort((left, right) => left.seq - right.seq)
        .map((event, index, ordered) => ({
            id: `catalogue-${event.seq}`,
            service: "catalogue",
            seq: event.seq,
            kind: lookup(CATALOGUE_KIND, event.kind) ?? kindWords(event.kind),
            at: event.occurred_at,
            tone: "board",
            detail: catalogueDetail(event, ordered[index - 1], timeZone),
            resumeId: null,
        }));
    const ordered = [...applicationEvents].sort((left, right) => left.seq - right.seq);
    const envoy: RecordEntry[] = ordered.map((event, index) => {
        const [kind, tone] = lookup(ENVOY_KIND, event.kind) ?? [
            kindWords(event.kind),
            "quiet" as RecordTone,
        ];
        const queuedBefore = ordered.slice(0, index).some((earlier) => earlier.kind === "QUEUED");
        return {
            id: `envoy-${event.seq}`,
            service: "envoy",
            seq: event.seq,
            kind,
            at: event.occurred_at,
            tone,
            detail: envoyDetail(event, modelName, queuedBefore),
            resumeId:
                event.kind === "TAILORED" || event.kind === "APPLIED"
                    ? text(event.payload, "resume_id")
                    : null,
        };
    });
    const rank = (entry: RecordEntry) => (entry.service === "catalogue" ? 0 : 1);
    return [...catalogue, ...envoy].sort(
        (left, right) =>
            new Date(left.at).getTime() - new Date(right.at).getTime() ||
            rank(left) - rank(right) ||
            left.seq - right.seq,
    );
}
